import PropTypes from 'prop-types';
import { BarChart2, Plus } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, IconHighlight } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

/**
 * Dashboards list header — title and primary action.
 *
 * Presentational: the page owns the scroll state and the sticky wrapper, the
 * same split Agentic Studio's networks list uses. This component only decides
 * what a compressed header shows.
 *
 * The Created/Templates tabs are deliberately **not** here: they belong to the
 * scrolling page, not to the pinned bar. See `DashboardListTabs`.
 *
 * It used to pin itself: a 1px sentinel watched by an `IntersectionObserver`,
 * a `ResizeObserver` measuring the scroll container, and a `fixed` bar
 * positioned from those measurements with an inline `top: 48px`. All of it is
 * gone — `position: sticky` inside the scroll container needs no measuring, and
 * the duplicate title and button the fixed bar carried went with it.
 */
const DashboardListHeader = ({ isScrolled }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboards');
  const handleNewDashboard = useCallback(() => navigate('/dashboards/new'), [navigate]);

  // Same shape as every other list page in the platform: the icon leads, and
  // `size: 'md'` overrides the `sm` a header at `M` would otherwise hand it.
  const primaryCta = useMemo(
    () => ({
      label: t('header.createButton'),
      onClick: handleNewDashboard,
      leftIcon: Plus,
      size: 'md',
    }),
    [handleNewDashboard, t],
  );
  return (
    <header>
      {/* `BarChart2` is the icon the shell's sidebar already gives this page —
          a page header that leads with a different one than the nav item you
          clicked to get here reads as a different page.

          No `showBackButton={false}`: the DS only renders it when `onBack` is
          supplied, so passing the flag says nothing. Agentic Studio omits it. */}
      <Header
        size={isScrolled ? 'compress' : 'M'}
        showIcon
        icon={<IconHighlight icon={BarChart2} bgColor="Primary" iconColor="PrimaryAlt" size="small" />}
        title={t('header.title')}
        showDescription={!isScrolled}
        description={t('header.description')}
        showTag={false}
        primaryCta={primaryCta}
      />
    </header>
  );
};

DashboardListHeader.propTypes = {
  isScrolled: PropTypes.bool,
};

export default DashboardListHeader;
